import { Injectable, inject } from '@angular/core';
import {BehaviorSubject, catchError, finalize, map, Observable, of, Subscription} from 'rxjs';
import {
  ApiLocationResponseDto,
  ApiWeatherForecastDayResponseDto,
  ApiWeatherForecastHourResponseDto,
  ApiWeatherForecastNowResponseDto,
  ApiWeatherForecastResponseDto,
  ApiWeatherForecastTodayResponseDto,
  Weather,
  WeatherForecastApiService
} from '@birco/rainmesh-api';
import {decimalToSexagesimal} from 'geolib';

@Injectable({
  providedIn: 'root'
})
export class WeatherService {
  private weatherForecastApiService = inject(WeatherForecastApiService);

  private subscription = new Subscription();

  private loading$$ = new BehaviorSubject<boolean>(false);
  private data$$ = new BehaviorSubject<ApiWeatherForecastResponseDto | null>(null);

  constructor() {
    this.subscription.add(this.initDataSubscription());
  }

  extendedForecast$(): Observable<ApiWeatherForecastDayResponseDto[]> {
    return this.data$$.asObservable()
      .pipe(
        map(d => d?.days ?? []),
      );
  }

  todayForecast$(): Observable<ApiWeatherForecastTodayResponseDto | null> {
    return this.data$$.asObservable()
      .pipe(
        map(d => d?.today ?? null)
      );
  }

  currentWeather$(): Observable<ApiWeatherForecastNowResponseDto | null> {
    return this.data$$.asObservable()
      .pipe(
        map(d => d?.now ?? null)
      );
  }

  hourlyForecast$(): Observable<ApiWeatherForecastHourResponseDto[]> {
    return this.data$$.asObservable()
      .pipe(
        map(d => d?.hours ?? []),
        map(hours => {
          const [startOfDayUTC, endOfDayUTC] = this.getDayRange();

          return hours.filter(item => {
            const ts = new Date(item.dateTime);
            return ts >= startOfDayUTC && ts <= endOfDayUTC;
          })
        })
      );
  }

  location$(): Observable<ApiLocationResponseDto> {
    return this.data$$.asObservable()
      .pipe(
        map(d => d?.location ?? {lat: 0, lon: 0})
      );
  }

  loading$(): Observable<boolean> {
    return this.loading$$.asObservable();
  }

  private initDataSubscription(): Subscription {
    this.loading$$.next(true);
    return this.weatherForecastApiService.getWeatherForecast()
      .pipe(
        catchError(error => {
          console.error('Fehler beim Laden der Daten:', error);
          return of(null);
        }),
        finalize(() => {
          this.loading$$.next(false);
        })
      ).subscribe(data => this.data$$.next(data));
  }

  utils = {
    getRainHint(weather: Weather): { error: boolean, text: string } | undefined {
      if (weather === Weather.HEAVY_RAIN) {
        return {error: true, text: 'weather.heavyRainWarning'}
      }
      return undefined
    },

    getWeatherIcon(weather: Weather): string {
      switch (weather) {
        case Weather.SUNNY:
          return 'sunny';
        case Weather.CLOUDY:
          return 'cloudy';
        case Weather.FAIR:
          return 'fair';
        case Weather.HEAVY_RAIN:
        case Weather.MODERATE_RAIN:
        case Weather.LIGHT_RAIN:
        case Weather.HAIL:
          return 'rain';
        case Weather.SNOW:
          return 'snow';
        case Weather.STORM:
          return 'storm';
        default:
          return ''
      }
    },

    getGeoInfo(lat: number, lon: number): string {
      if (!lat || !lon) return ''
      return `${decimalToSexagesimal(lat)},<br> ${decimalToSexagesimal(lon)}`
    }
  }

  private getDayRange(date: Date = new Date()): [Date, Date] {
    const start = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0);
    const end = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59);
    return [start, end];
  }
}
